import { store } from "utils/store";
import { refreshFeatures } from "redux/featuresSlice";

const NOT_ON_PLAN_PATTERN = /not available on your plan/i;
const FEATURE_KEY_PATTERN = /Feature\s+'([^']+)'/i;

export interface PlanForbiddenInfo {
  isPlanForbidden: boolean;
  featureKey?: string;
  message?: string;
}

export interface AdminDisabledInfo {
  isAdminDisabled: boolean;
  message?: string;
}

const readStatus = (error: any): number | undefined => {
  const raw = error?.status ?? error?.code ?? error?.statusCode;
  if (raw === undefined || raw === null) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

const readMessage = (error: any): string | undefined => {
  return (
    error?.message ??
    error?.errorMessage ??
    error?.error?.message ??
    (typeof error === "string" ? error : undefined)
  );
};

/**
 * Detects the backend's plan-gate 403 shape:
 *   { code: 403, message: "Feature 'envelope.bulk_send' is not available on your plan" }
 */
export const parsePlanForbidden = (error: any): PlanForbiddenInfo => {
  const status = readStatus(error);
  const message = readMessage(error);

  if (status !== 403) return { isPlanForbidden: false };
  if (!message || !NOT_ON_PLAN_PATTERN.test(message)) {
    return { isPlanForbidden: false };
  }
  const m = FEATURE_KEY_PATTERN.exec(message);
  return {
    isPlanForbidden: true,
    featureKey: m?.[1],
    message,
  };
};

export const isPlanForbidden = (error: any): boolean =>
  parsePlanForbidden(error).isPlanForbidden;

export const handlePlanForbidden = (error: any): PlanForbiddenInfo => {
  const info = parsePlanForbidden(error);
  if (info.isPlanForbidden) {
    store.dispatch(refreshFeatures());
  }
  return info;
};

/**
 * Detects the backend's admin-disabled 423 shape:
 *   { code: 423, message: "..." }  — message is the admin-supplied disabledMessage
 */
export const parseAdminDisabled = (error: any): AdminDisabledInfo => {
  const status = readStatus(error);
  if (status !== 423) return { isAdminDisabled: false };
  return { isAdminDisabled: true, message: readMessage(error) };
};

export const isAdminDisabled = (error: any): boolean =>
  parseAdminDisabled(error).isAdminDisabled;

/**
 * Unified handler for feature-related errors in catch blocks.
 * Routes 403 → upgrade modal data, 423 → maintenance banner data.
 * Dispatches refreshFeatures() for both so local Redux state catches up.
 */
export const handleFeatureError = (
  error: any
): {
  isPlanForbidden: boolean;
  isAdminDisabled: boolean;
  featureKey?: string;
  message?: string;
} => {
  const plan = parsePlanForbidden(error);
  const admin = parseAdminDisabled(error);
  if (plan.isPlanForbidden || admin.isAdminDisabled) {
    store.dispatch(refreshFeatures());
  }
  return {
    isPlanForbidden: plan.isPlanForbidden,
    isAdminDisabled: admin.isAdminDisabled,
    featureKey: plan.featureKey,
    message: plan.message ?? admin.message,
  };
};
